import React from 'react';
import PropTypes from 'prop-types';

import {Box, Paper, Stack, Typography} from 'views/components';
import {WarningAmberOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import formatters from '../shared/formatters';
import {getPeriod, PERIOD_LABEL_EXPRESSIONS, PERIOD_ROLES} from '../shared/periods';

const FLAG_ACCENT_COLOR = '#B57A2E';

const isPositive = (value) => !formatters.isMissingValue(value) && value > 0;

// Builds the labelled warning rows for one period. Rate/unassigned counts come from the
// per-period dataQuality object; unpriced scheduled hours live on the period's `scheduled`
// aggregate; "actual data unavailable" is only a warning for a period that should have
// actuals (never the next month, which legitimately has none).
const buildPeriodRows = (dataQuality, period, role) => {
  if (!dataQuality && !period) {
    return [];
  }

  const dq = dataQuality || {};
  const rows = [];

  if (isPositive(dq.missingClientRateCount)) {
    rows.push({
      key: 'missingClientRates',
      label: translate('metrics.ClientMetrics.DataQuality.MissingClientRates'),
      value: translate('metrics.ClientMetrics.DataQuality.CountHoursValue', {
        count: formatters.formatCount(dq.missingClientRateCount),
        hours: formatters.formatHours(dq.missingClientRateHours),
      }),
    });
  }

  if (isPositive(dq.missingCaregiverRateCount)) {
    rows.push({
      key: 'missingCaregiverRates',
      label: translate('metrics.ClientMetrics.DataQuality.MissingCaregiverRates'),
      value: translate('metrics.ClientMetrics.DataQuality.CountHoursValue', {
        count: formatters.formatCount(dq.missingCaregiverRateCount),
        hours: formatters.formatHours(dq.missingCaregiverRateHours),
      }),
    });
  }

  const unpricedHours = period?.scheduled?.unpricedClientHours;

  if (isPositive(unpricedHours)) {
    rows.push({
      key: 'unpricedHours',
      label: translate('metrics.ClientMetrics.DataQuality.UnpricedHours'),
      value: translate('metrics.ClientMetrics.DataQuality.HoursValue', {hours: formatters.formatHours(unpricedHours)}),
    });
  }

  if (isPositive(dq.unassignedEventCount)) {
    rows.push({
      key: 'unassignedEvents',
      label: translate('metrics.ClientMetrics.DataQuality.UnassignedEvents'),
      value: isPositive(dq.unassignedRevenue) ?
        translate('metrics.ClientMetrics.DataQuality.UnassignedValue', {
          count: formatters.formatCount(dq.unassignedEventCount),
          revenue: formatters.formatCurrency(dq.unassignedRevenue),
        }) :
        translate('metrics.ClientMetrics.DataQuality.CountValue', {count: formatters.formatCount(dq.unassignedEventCount)}),
    });
  }

  // The next month has no actuals by design, so an unavailable flag there is expected, not
  // a warning.
  if (role !== 'next' && dq.actualDataAvailable === false) {
    rows.push({
      key: 'missingActuals',
      label: translate('metrics.ClientMetrics.DataQuality.MissingActuals'),
      value: null,
    });
  }

  return rows;
};

// Skipped schedule records are reported once at the snapshot level (not per period), with
// an optional per-reason breakdown.
const buildSnapshotRows = (dataQuality) => {
  if (!dataQuality || !isPositive(dataQuality.skippedEventCount)) {
    return [];
  }

  const reasons = Object.entries(dataQuality.skippedEventReasons || {})
      .map(([reason, count]) => translate('metrics.ClientMetrics.DataQuality.SkippedReasonItem', {
        reason,
        count: formatters.formatCount(count),
      }));

  return [{
    key: 'skippedEvents',
    label: translate('metrics.ClientMetrics.DataQuality.SkippedEvents'),
    value: translate('metrics.ClientMetrics.DataQuality.CountValue', {count: formatters.formatCount(dataQuality.skippedEventCount)}),
    detail: reasons.length ? reasons.join(', ') : null,
  }];
};

// Monthly data-quality panel grouped by previous / current / next, plus a snapshot-wide
// group for skipped records. The whole section is hidden only when every group is clean;
// otherwise each period is listed so a clean period is explicitly shown as clean.
const MonthlyDataQualitySection = (props) => {
  const {
    isLoading,
    snapshot,
  } = props;

  const groups = React.useMemo(() => {
    const dataQuality = snapshot?.dataQuality || null;

    const periodGroups = PERIOD_ROLES.map((role) => ({
      key: role,
      labelExpression: PERIOD_LABEL_EXPRESSIONS[role],
      rows: buildPeriodRows(dataQuality?.[role] || null, getPeriod(snapshot, role), role),
    }));

    const snapshotRows = buildSnapshotRows(dataQuality);

    if (snapshotRows.length) {
      periodGroups.push({
        key: 'snapshot',
        labelExpression: 'metrics.ClientMetrics.DataQuality.Snapshot',
        rows: snapshotRows,
      });
    }

    return periodGroups;
  }, [snapshot]);

  const hasAnyIssues = groups.some((group) => group.rows.length > 0);

  if (isLoading || !snapshot || !hasAnyIssues) {
    return null;
  }

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.DataQuality.SectionTitle' />
      <Paper
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
          boxShadow: 'none',
        }}
      >
        <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#A8A49E', mb: 2}}>
          <Translate expression='metrics.ClientMetrics.DataQuality.Intro' />
        </Typography>
        <Stack spacing={2.5}>
          {
            groups.map((group) => (
              <Box key={group.key}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    color: '#6B6761',
                    mb: 1,
                  }}
                >
                  <Translate expression={group.labelExpression} />
                </Typography>
                {
                  !group.rows.length &&
                  <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#A8A49E'}}>
                    <Translate expression='metrics.ClientMetrics.DataQuality.PeriodClean' />
                  </Typography>
                }
                {
                  group.rows.map((row, index) => (
                    <Stack
                      key={row.key}
                      direction='row'
                      justifyContent='space-between'
                      alignItems='flex-start'
                      spacing={2}
                      sx={{
                        py: 1,
                        borderBottom: index === group.rows.length - 1 ? 'none' : '1px solid #F2F0EC',
                      }}
                    >
                      <Stack direction='row' alignItems='flex-start' spacing={1} sx={{minWidth: 0}}>
                        <WarningAmberOutlined sx={{fontSize: 16, color: FLAG_ACCENT_COLOR, flexShrink: 0, mt: '1px'}} />
                        <Box sx={{minWidth: 0}}>
                          <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#1A1916'}}>
                            {row.label}
                          </Typography>
                          {
                            row.detail &&
                            <Typography sx={{fontSize: '11px', lineHeight: '15px', color: '#A8A49E'}}>
                              {row.detail}
                            </Typography>
                          }
                        </Box>
                      </Stack>
                      {
                        row.value &&
                        <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#6B6761', textAlign: 'right', whiteSpace: 'nowrap'}}>
                          {row.value}
                        </Typography>
                      }
                    </Stack>
                  ))
                }
              </Box>
            ))
          }
        </Stack>
      </Paper>
    </Box>
  );
};

MonthlyDataQualitySection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  snapshot: PropTypes.object,
};

export default React.memo(MonthlyDataQualitySection);
